// bookings.prefered_date stores a full ISO timestamp, but every consumer reads
// only the date half via LEFT(prefered_date,10) — the real booking time lives in
// the prefered_time FK. Because of that, the time portion was written as a
// hardcoded `T14:37:00.116` on every row, so the column carried a time that was
// both untrue and identical everywhere, leaving same-day bookings unsortable and
// trapping anything that ever read the timestamp in full.
//
// Build it from the slot the user actually picked instead. The date half is
// unchanged, so LEFT(prefered_date,10) keeps behaving exactly as before.

const CLOCK = /^\d{1,2}:\d{2}$/;

// `time` may be a clock string ("22:00") on the create screens or a
// time_zones.id (32) on the edit screens, which load the value back from the DB.
export const resolvePreferredClock = (time, slots) => {
  const raw = String(time ?? '').trim();
  if (CLOCK.test(raw)) return raw.padStart(5, '0');

  const match = (Array.isArray(slots) ? slots : []).find(
    (slot) => String(slot?.id) === raw,
  );
  const clock = String(match?.time_zone ?? '').trim();
  return CLOCK.test(clock) ? clock.padStart(5, '0') : '';
};

export const buildPreferredDate = (date, time, slots, utcOffset) => {
  const day = String(date ?? '').trim();
  // Already a full timestamp (edit screens re-submit what they loaded).
  if (day.length !== 10) return date;

  // Midnight is the honest fallback when the slot can't be resolved: it keeps
  // the date half correct rather than inventing a time that looks real.
  const clock = resolvePreferredClock(time, slots) || '00:00';
  return `${day}T${clock}:00.000${utcOffset}`;
};

// "+05:30" → 330. Falls back to the browser's offset when the value is unusable.
const offsetMinutes = (utcOffset) => {
  const match = /^([+-])(\d{2}):?(\d{2})$/.exec(String(utcOffset ?? '').trim());
  if (!match) return -new Date().getTimezoneOffset();
  const minutes = Number(match[2]) * 60 + Number(match[3]);
  return match[1] === '-' ? -minutes : minutes;
};

// Today's date (YYYY-MM-DD) on the tenant's clock, not the browser's.
export const tenantToday = (utcOffset, now = Date.now()) =>
  new Date(now + offsetMinutes(utcOffset) * 60000).toISOString().slice(0, 10);

// True when the picked date + slot is already in the past on the tenant's clock.
// Without a resolvable slot only the date is compared.
export const isPastSlot = (date, time, slots, utcOffset, now = Date.now()) => {
  const day = String(date ?? '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return false;
  const clock = resolvePreferredClock(time, slots);
  if (!clock) return day < tenantToday(utcOffset, now);
  const sign = offsetMinutes(utcOffset) < 0 ? '-' : '+';
  const abs = Math.abs(offsetMinutes(utcOffset));
  const offset = `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
  return Date.parse(`${day}T${clock}:00.000${offset}`) < now;
};
